import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { onlyBranch } from '@/lib/singleBranch';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { participantLabel, labelFor, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Plus, DollarSign, Eye, Wallet, CheckCircle2, FileText } from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { IconButton } from '@/components/common/IconButton';
import { toast } from 'sonner';

export default function FundingPlans() {
  const [plans, setPlans] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ledgerDialog, setLedgerDialog] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({ participant_id: '', plan_name: '', start_date: '', end_date: '', total_budget: '', state_id: '', branch_id: '' });
  useEffect(() => {   // one branch: fill it in (the picker is hidden)
    const only = onlyBranch(branches);
    if (only && !form.branch_id) setForm((f) => ({ ...f, branch_id: only.code }));
  }, [branches, form.branch_id]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      const [plansRes, participantsRes, statesRes, branchesRes] = await Promise.all([
        api.get('/funding/plans'),
        api.get('/participants'),
        api.get('/org/states'),
        api.get('/org/branches'),
      ]);
      setPlans(plansRes.data.data || []);
      setParticipants(participantsRes.data.data || []);
      setStates(statesRes.data.data || []);
      setBranches(branchesRes.data.data || []);
    } catch (e) {
      toast.error('Failed to load funding plans');
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/funding/plans', { ...form, total_budget: parseFloat(form.total_budget) });
      toast.success('Funding plan created');
      setDialogOpen(false);
      setForm({ participant_id: '', plan_name: '', start_date: '', end_date: '', total_budget: '', state_id: '', branch_id: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create plan');
    } finally {
      setLoading(false);
    }
  };

  const viewLedger = async (plan) => {
    setLedgerDialog(plan);
    try {
      const res = await api.get(`/funding/plans/${plan.id}/ledger`);
      setLedgerEntries(res.data.data || []);
    } catch (e) {
      toast.error('Failed to load ledger');
    }
  };

  const getBudgetStatus = (plan) => {
    const pct = ((plan.remaining_budget || 0) / (plan.total_budget || 1)) * 100;
    if (pct > 50) return 'healthy';
    if (pct > 20) return 'warning';
    return 'critical';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="funding-plans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Funding Plans</h1>
          <p className="text-muted-foreground mt-1">NDIS funding plan management and ledger tracking</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="funding-create-button">
              <Plus className="h-4 w-4 mr-2" /> New Plan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create Funding Plan</DialogTitle><DialogDescription>Create a new NDIS funding plan for a participant</DialogDescription></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Participant</Label>
                <AsyncSearchSelect
                  endpoint="/participants"
                  value={form.participant_id}
                  onChange={(v) => setForm({...form, participant_id: v})}
                  getOptionLabel={p => `${participantLabel(p)}${p.ndis_number ? ` (${p.ndis_number})` : ''}`}
                  selectedLabel={labelFor(participants, form.participant_id, participantLabel)}
                  placeholder="Select participant"
                  searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                  emptyText="No participants match that search"
                  testId="funding-participant-select"
                />
              </div>
              <div className="space-y-2">
                <Label>Plan Name</Label>
                <Input value={form.plan_name} onChange={(e) => setForm({...form, plan_name: e.target.value})} required placeholder="e.g. Core Plan 2026" data-testid="funding-planname-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Total Budget ($)</Label>
                <Input type="number" step="0.01" value={form.total_budget} onChange={(e) => setForm({...form, total_budget: e.target.value})} required placeholder="50000" data-testid="funding-budget-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>State</Label>
                  <Select value={form.state_id} onValueChange={(v) => setForm({...form, state_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent>{states.map((s) => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {!onlyBranch(branches) && (   /* one branch: filled in automatically, no picker */
                <div className="space-y-2"><Label>Branch</Label>
                  <Select value={form.branch_id} onValueChange={(v) => setForm({...form, branch_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>{branches.map((b) => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="funding-submit-button">
                {loading ? 'Creating...' : 'Create Plan'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI band — additive only */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="funding-plans-summary">
        <div className="rounded-lg border bg-white p-3" data-testid="funding-plans-summary-total">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Total plans</span>
            <FileText className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{plans.length}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="funding-plans-summary-budget">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Total budget</span>
            <Wallet className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-emerald-700">${plans.reduce((s, p) => s + (p.total_budget || 0), 0).toLocaleString()}</div>
        </div>
        <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3" data-testid="funding-plans-summary-remaining">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Remaining</span>
            <DollarSign className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-sky-700">${plans.reduce((s, p) => s + (p.remaining_budget || 0), 0).toLocaleString()}</div>
        </div>
        <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="funding-plans-summary-healthy">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Healthy</span>
            <CheckCircle2 className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-violet-700">{plans.filter(p => getBudgetStatus(p) === 'healthy').length}</div>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {plans.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title="No funding plans yet"
              description="Create a funding plan to start tracking NDIS budgets and ledger entries for a participant."
              action={<Button size="sm" onClick={() => setDialogOpen(true)}><Plus className="h-4 w-4 mr-1" />New Plan</Button>}
              testid="funding-plans-empty"
            />
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Participant</TableHead>
                <TableHead>Plan Name</TableHead>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Total Budget</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plans.map((p) => (
                <TableRow key={p.id} className="hover:bg-muted/60" data-testid={`funding-plan-row-${p.id}`}>
                  <TableCell className="font-medium">{p.participant_name || 'N/A'}</TableCell>
                  <TableCell>{p.plan_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.start_date} to {p.end_date}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">${(p.total_budget || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums" data-testid="funding-ledger-running-balance-text">${(p.remaining_budget || 0).toLocaleString()}</TableCell>
                  <TableCell><StatusBadge status={getBudgetStatus(p) === 'healthy' ? 'active' : getBudgetStatus(p) === 'warning' ? 'SUBMITTED' : 'REJECTED'} /></TableCell>
                  <TableCell>
                    <IconButton label="View ledger" onClick={() => viewLedger(p)} testid={`funding-view-ledger-${p.id}`}>
                      <Eye className="h-3.5 w-3.5" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>

      {/* Ledger Dialog */}
      <Dialog open={!!ledgerDialog} onOpenChange={() => setLedgerDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Funding Ledger - {ledgerDialog?.plan_name}</DialogTitle>
            <DialogDescription>Transaction history for this funding plan</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <Card><CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-lg font-bold tabular-nums">${(ledgerDialog?.total_budget || 0).toLocaleString()}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Remaining</p>
              <p className="text-lg font-bold tabular-nums text-primary">${(ledgerDialog?.remaining_budget || 0).toLocaleString()}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Spent</p>
              <p className="text-lg font-bold tabular-nums text-destructive">${((ledgerDialog?.total_budget || 0) - (ledgerDialog?.remaining_budget || 0)).toLocaleString()}</p>
            </CardContent></Card>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ledgerEntries.length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center py-4 text-muted-foreground">No entries</TableCell></TableRow>
              ) : ledgerEntries.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="text-xs">{e.created_at?.split('T')[0]}</TableCell>
                  <TableCell className="text-sm">{e.description}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-destructive">-${(e.amount || 0).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
